import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Phone, Mail, Clock, Send } from "lucide-react";
import { BRAND } from "../../lib/constants";
import { getAttribution, trackConversion, contactConversionRef, trackPhoneCall, callConversionRef } from "../../lib/attribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#F4F4F1] border-2 border-[#122A1E] px-4 py-3 font-body text-[#0A0E0C] placeholder:text-[#4A5550]/60 focus:outline-none focus:border-[#FF5A00] focus:shadow-[4px_4px_0px_0px_#FF5A00] transition-shadow rounded-none";
const labelCls = "font-mono-accent text-[10px] uppercase text-[#4A5550] mb-2 block tracking-[0.2em]";

export default function Contact() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please include name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      const attribution = getAttribution() || {};
      await axios.post(`${API}/contact`, { ...form, ...attribution });
      trackConversion(contactConversionRef());
      toast.success("Message sent. We'll be in touch shortly.");
      setForm({ full_name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-[#EAEAE5] border-b-2 border-[#122A1E]" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="font-mono-accent text-[11px] uppercase text-[#4A5550] mb-3">
              ◆ 006 · Contact
            </div>
            <h2 className="font-display font-black uppercase tracking-tighter leading-none text-[#0A0E0C] text-4xl md:text-5xl">
              Talk To Dispatch.
            </h2>
            <p className="mt-5 text-[#4A5550] leading-relaxed max-w-sm">
              Got a weird load? A tight timeline? A run nobody else will touch?
              That's our kind of call.
            </p>

            <div className="mt-8 space-y-4">
              <a
                href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
                onClick={() => trackPhoneCall(callConversionRef())}
                className="flex items-center gap-4 border-2 border-[#122A1E] bg-[#F4F4F1] p-4 hover-lift"
                data-testid="contact-phone"
              >
                <div className="w-10 h-10 bg-[#FF5A00] text-white flex items-center justify-center">
                  <Phone size={16} />
                </div>
                <div>
              <div className="font-mono-accent text-[10px] uppercase text-[#4A5550]">Call Dispatch</div>
                  <div className="font-display font-bold text-[#0A0E0C]">{BRAND.phone}</div>
                </div>
              </a>

              <a
                href={`mailto:${BRAND.email}`}
                className="flex items-center gap-4 border-2 border-[#122A1E] bg-[#F4F4F1] p-4 hover-lift"
                data-testid="contact-email"
              >
                <div className="w-10 h-10 bg-[#122A1E] text-white flex items-center justify-center">
                  <Mail size={16} />
                </div>
                <div>
                  <div className="font-mono-accent text-[10px] uppercase text-[#4A5550]">Email</div>
                  <div className="font-display font-bold text-[#0A0E0C]">{BRAND.email}</div>
                </div>
              </a>

              <div className="flex items-center gap-4 border-2 border-[#D2CEBD] bg-[#F4F4F1] p-4">
                <div className="w-10 h-10 bg-[#D2CEBD] text-[#122A1E] flex items-center justify-center">
                  <Clock size={16} />
                </div>
                <div>
                  <div className="font-mono-accent text-[10px] uppercase text-[#4A5550]">Hours</div>
                  <div className="font-display font-bold text-[#0A0E0C]">{BRAND.hours}</div>
                </div>
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            className="lg:col-span-7 border-2 border-[#122A1E] bg-[#F4F4F1] p-6 md:p-10 hard-shadow"
            data-testid="contact-form"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>Full Name *</label>
                <input type="text" required value={form.full_name} onChange={update("full_name")} className={inputCls} data-testid="contact-input-name" />
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input type="email" required value={form.email} onChange={update("email")} className={inputCls} data-testid="contact-input-email" />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input type="tel" value={form.phone} onChange={update("phone")} className={inputCls} data-testid="contact-input-phone" />
              </div>
              <div>
                <label className={labelCls}>Subject</label>
                <input type="text" value={form.subject} onChange={update("subject")} className={inputCls} placeholder="Quote follow-up / General" data-testid="contact-input-subject" />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Message *</label>
                <textarea rows={5} required value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} data-testid="contact-input-message" />
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-[#122A1E] hover:bg-[#0C1D14] disabled:opacity-60 text-white font-display font-bold uppercase text-sm tracking-wider px-7 py-4 border-2 border-[#122A1E] hover-lift"
                data-testid="contact-form-submit"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {submitting ? "Sending…" : "Send Message"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
